import React, { useRef, useState, useEffect } from "react"
import {
  PauseFill,
  PlayFill,
  PlusCircle,
  Repeat,
  Repeat1,
  Shuffle,
  SkipForwardFill,
  SkipBackwardFill,
  XCircle,
  ArrowsFullscreen,
} from "react-bootstrap-icons"
import ReactPlayer from "react-player"
import screenfull from "screenfull"
import { findDOMNode } from "react-dom"
import { Link } from "react-router-dom"
import styles from "./styles.module.scss"

import { timeToMinutesSeconds } from "../../util/dates"

import { postUserInteractionHeartbeat } from "../../mutations/postUserInteraction"

import { useSelector, useDispatch } from "react-redux"
import isEqual from "is-equal"
import watch from "redux-watch"
import store from "../../stores/store"
import {
  selectMediaPlayer,
  pause,
  playNext,
  playPrevious,
  playOrPause,
  removeElement,
  clearQueue,
  toggleShuffle,
  toggleLoop,
} from "../../stores/mediaPlayerSlice"
import { openSongActions } from "../../stores/songActionsSlice"
import { isMobile } from "react-device-detect"
import clsx from "clsx"

let currentTimeTimer = null

const PLAY_ID_STORAGE_KEY = "currentPlayId"

// currentMediaItem survives a full page reload (mediaPlayerSlice seeds its
// initial state from localStorage), so resuming a paused song after a reload
// never fires the "media item changed" branch below - it looks identical to
// an ordinary play/pause toggle. Without persisting play_id the same way,
// every heartbeat after a reload would silently no-op (sendHeartbeat bails
// when currentPlayId is null), leaving the row frozen at whatever result it
// had before the reload regardless of how much more gets listened to.
const loadPersistedPlayId = () => {
  try {
    const persisted = JSON.parse(localStorage.getItem(PLAY_ID_STORAGE_KEY))
    const mediaItem = JSON.parse(localStorage.getItem("currentMediaItem"))
    if (persisted && mediaItem && persisted.postId === mediaItem.postId) {
      return persisted.playId
    }
  } catch (e) {
    // malformed localStorage - fall through to starting fresh
  }
  return null
}

// Identifies one specific playback attempt (regenerated every time the
// current media item changes) so heartbeats for this song can never be
// confused with a different playback of the same song, unlike the old
// activity-window-based matching this replaced.
let currentPlayId = loadPersistedPlayId()

const setCurrentPlayId = (playId, postId) => {
  currentPlayId = playId
  localStorage.setItem(PLAY_ID_STORAGE_KEY, JSON.stringify({ playId, postId }))
}

let heartbeatTickCount = 0
// Snapshotted every second while playing (see onPlaying) rather than read
// live off the audio/video element at finalize time, because onSkipClick,
// onPreviousClick, and the onended handler all call clearMediaRefs() - which
// zeroes the element's position - synchronously, before the Redux watcher
// below ever runs. A live read at that point would always see 0.
let lastKnownPositionSeconds = 0
const HEARTBEAT_INTERVAL_SECONDS = 15
const active = "#a9a9a9"
const inactive = "#dbdbdb"

const currentPlaybackPositionSeconds = (mediaItem, audioPlayerRef, videoPlayerRef) => {
  if (!mediaItem) return 0
  if (mediaItem.type === "audio") return audioPlayerRef.current?.currentTime || 0
  if (mediaItem.type === "video") return videoPlayerRef.current?.getCurrentTime() || 0
  return 0
}

const sendHeartbeat = (mediaItem, positionSeconds, requestOptions = {}) => {
  if (!mediaItem || !currentPlayId) return
  postUserInteractionHeartbeat(
    mediaItem.bandId,
    currentPlayId,
    {
      postId: mediaItem.postId,
      postType: mediaItem.type,
      positionSeconds: Math.round(positionSeconds || 0),
      playlistId: mediaItem.playlistId,
    },
    requestOptions,
  )
}

const Scrubber = ({ current, max, playerCallback }) => {
  const inputRef = useRef(null)

  const handleChange = (min, max) => (event) => {
    playerCallback(event.target.value)
  }

  const minValue = 0
  const maxValue = max
  const progress = (current / maxValue) * 100 + "%"

  const styleInput = {
    background: `linear-gradient(90deg, ${active} 0% ${progress},   ${inactive} ${progress} 100%)`,
  }

  return (
    <input
      ref={inputRef}
      id="sliderId"
      className="scrubber"
      name="sliderName"
      type="range"
      min={minValue}
      max={maxValue}
      value={current}
      onChange={handleChange(minValue, maxValue)}
      style={styleInput}
    />
  )
}

export const UniversalPlayer = () => {
  const dispatch = useDispatch()
  const mediaPlayerState = useSelector(selectMediaPlayer)
  const audioPlayerRef = useRef(null)
  const videoPlayerRef = useRef(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [mobileView, setMobileView] = useState("controls")
  const [titleHovered, setTitleHovered] = useState(false)
  const size = 30

  const hasInteracted = useRef(false)
  let videoAutoplay = false
  let initialLoad = true

  const clearMediaRefs = () => {
    clearInterval(currentTimeTimer)
    scrubberUpdate(0)
  }

  const playCurrent = (mediaItem) => {
    if (mediaItem && mediaItem.type === "audio") {
      audioPlayerRef.current.play()
    } else if (mediaItem && mediaItem.type === "video") {
      const internalPlayer =
        videoPlayerRef && videoPlayerRef.current
          ? videoPlayerRef.current.getInternalPlayer()
          : null
      if (internalPlayer) internalPlayer.playVideo()
    }
  }

  const pauseCurrent = (mediaItem) => {
    if (mediaItem && mediaItem.type === "audio") {
      audioPlayerRef.current.pause()
    } else if (mediaItem && mediaItem.type === "video") {
      const internalPlayer =
        videoPlayerRef && videoPlayerRef.current
          ? videoPlayerRef.current.getInternalPlayer()
          : null
      if (internalPlayer) internalPlayer.pauseVideo()
    }
  }

  const onPlayOrPauseClick = () => {
    hasInteracted.current = true
    dispatch(playOrPause())
  }

  const onSkipClick = () => {
    clearMediaRefs()
    if (mediaPlayerState.loopMode === "song" && mediaPlayerState.currentMediaItem) {
      dispatch(playNext())
      onPlaying(mediaPlayerState.currentMediaItem)
      return
    }
    dispatch(playNext())
  }

  const onPreviousClick = () => {
    if (currentTime > 3 || mediaPlayerState.history.length === 0) {
      scrubberUpdate(0)
    } else {
      clearMediaRefs()
      dispatch(playPrevious())
    }
  }

  const onShuffleClick = () => {
    dispatch(toggleShuffle())
  }

  const onLoopClick = () => {
    dispatch(toggleLoop())
  }

  const loopTitle = () => {
    if (mediaPlayerState.loopMode === "song") return "Loop: Song"
    const hasPlaylist = !!mediaPlayerState.currentMediaItem?.playlistId
    return mediaPlayerState.loopMode === "context"
      ? `Loop: ${hasPlaylist ? "Playlist" : "Album"}`
      : `Loop: Off (click to loop song)`
  }

  const onRemoveFromQueue = (index) => {
    dispatch(removeElement(index))
  }

  const onPlaying = (mediaItem) => {
    if (currentTimeTimer) clearInterval(currentTimeTimer)
    heartbeatTickCount = 0
    currentTimeTimer = setInterval(() => {
      const position = currentPlaybackPositionSeconds(mediaItem, audioPlayerRef, videoPlayerRef)
      setCurrentTimeWrapper(position)
      lastKnownPositionSeconds = position

      heartbeatTickCount += 1
      if (heartbeatTickCount >= HEARTBEAT_INTERVAL_SECONDS) {
        heartbeatTickCount = 0
        sendHeartbeat(mediaItem, position)
      }
    }, 1000)
    playCurrent(mediaItem)
  }

  const scrubberUpdate = (newTime) => {
    if (audioPlayerRef && audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = newTime
      setCurrentTimeWrapper(audioPlayerRef.current.currentTime)
    }
    if (
      videoPlayerRef &&
      videoPlayerRef.current &&
      videoPlayerRef.current.getDuration()
    ) {
      videoPlayerRef.current.seekTo(newTime, "seconds")
      setCurrentTimeWrapper(newTime)
      playCurrent(mediaPlayerState.currentMediaItem)
    }
  }

  const onClearQueueClick = () => {
    dispatch(clearQueue())
  }

  const openSongActionsForCurrent = () => {
    const item = mediaPlayerState.currentMediaItem
    if (!item) return
    dispatch(openSongActions({
      id: item.postId,
      title: item.title,
      nada_url: item.nadaPath
        ? `${window.location.origin}${item.nadaPath}`
        : item.nadaPath,
      nada_path: item.nadaPath,
      streaming_url: item.streamingUrl,
      band_id: item.bandId,
      band_name: item.bandName,
      album_id: item.albumId,
      album_name: item.albumName,
      album_path: item.albumPath,
      cover_image_small: item.coverImageSmall,
      local_path: item.localPath,
      duration: item.duration,
      playlistId: item.playlistId,
    }))
  }

  const onClearAndCloseClick = () => {
    dispatch(clearQueue())
    onSkipClick()
  }

  const setCurrentTimeWrapper = (currentTime) => {
    localStorage.setItem("currentTime", JSON.stringify(currentTime))
    setCurrentTime(currentTime)
  }

  const onVideoError = (e) => {
    console.error("Video error!", e)
  }

  const onVideoPlaying = () => {
    if (initialLoad) {
      const savedTime = JSON.parse(localStorage.getItem("currentTime"))
      if (videoPlayerRef && videoPlayerRef.current) {
        videoPlayerRef.current.seekTo(savedTime, "seconds")
      }
      initialLoad = false
    }

    if (!currentTimeTimer) {
      currentTimeTimer = setInterval(() => {
        if (
          videoPlayerRef &&
          videoPlayerRef.current &&
          mediaPlayerState.currentMediaItem &&
          mediaPlayerState.currentMediaItem?.type === "video"
        ) {
          setCurrentTimeWrapper(videoPlayerRef.current.getCurrentTime())
        } else {
          setCurrentTimeWrapper(0)
        }
      }, 1000)
    }
  }

  const onVideoPause = () => {}

  const handleClickFullscreen = () => {
    // @ts-ignore
    screenfull.request(findDOMNode(videoPlayerRef.current))
  }

  const playerStyle = {
    position: "fixed",
    left: 0,
    bottom: 0,
    height: "100px",
    width: "100%",
    textAlign: "center",
    color: "white",
    backgroundColor: "#2d2d2d",
    zIndex: 20,
  }

  const metaDataStyle = {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    justifyContent: "space-between",
  }

  if (!mediaPlayerState.queue.length && !mediaPlayerState.currentMediaItem) {
    playerStyle.visibility = "hidden"
  }

  useEffect(() => {
    dispatch(pause())
  }, [])

  // navigator.sendBeacon can't carry the Authorization header this API
  // requires and is POST-only, so the teardown flush uses fetch's keepalive
  // flag instead - it gets the same "survive page unload" guarantee while
  // still supporting PUT and custom headers.
  useEffect(() => {
    const flushCurrentHeartbeat = () => {
      const state = store.getState().mediaPlayer.value
      if (state.playerState !== "playing" || !state.currentMediaItem) return

      sendHeartbeat(state.currentMediaItem, lastKnownPositionSeconds, { keepalive: true })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushCurrentHeartbeat()
    }

    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("pagehide", flushCurrentHeartbeat)
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("pagehide", flushCurrentHeartbeat)
    }
  }, [])

  useEffect(() => {
    const savedTime = JSON.parse(localStorage.getItem("currentTime"))
    if (mediaPlayerState.currentMediaItem) {
      if (mediaPlayerState.currentMediaItem?.type === "audio") {
        audioPlayerRef.current.src =
          mediaPlayerState.currentMediaItem?.streamingUrl
        audioPlayerRef.current.onended = () => {
          onSkipClick()
        }
        audioPlayerRef.current.currentTime = savedTime
        setCurrentTime(savedTime)
      } else if (mediaPlayerState.currentMediaItem?.type === "video") {
        setCurrentTime(savedTime)
      }
    }

    let w = watch(store.getState, "mediaPlayer", isEqual)
    store.subscribe(
      w((newVal, oldVal, objectPath) => {
        if (
          oldVal.value.currentMediaItem?.streamingUrl !==
          newVal.value.currentMediaItem?.streamingUrl
        ) {
          if (oldVal.value.currentMediaItem) {
            sendHeartbeat(oldVal.value.currentMediaItem, lastKnownPositionSeconds)
          }

          clearMediaRefs()

          if (newVal.value.currentMediaItem) {
            localStorage.setItem("endTime", JSON.stringify(0))
            setCurrentPlayId(crypto.randomUUID(), newVal.value.currentMediaItem.postId)
            heartbeatTickCount = 0
            lastKnownPositionSeconds = 0
            sendHeartbeat(newVal.value.currentMediaItem, 0)

            if (newVal.value.currentMediaItem?.type === "audio") {
              audioPlayerRef.current.src =
                newVal.value.currentMediaItem?.streamingUrl
              audioPlayerRef.current.onended = () => {
                onSkipClick()
              }
              if (newVal.value.playerState === "playing") {
                onPlaying(newVal.value.currentMediaItem)
                return
              }
            } else {
              audioPlayerRef.current.src = ""
            }
          }
        }

        if (
          newVal.value.playerState &&
          newVal.value.playerState !== oldVal.value.playerState
        ) {
          if (newVal.value.playerState === "playing") {
            onPlaying(newVal.value.currentMediaItem)
          }

          if (
            newVal.value.playerState === "paused" ||
            newVal.value.playerState === "idle"
          ) {
            clearInterval(currentTimeTimer)
            pauseCurrent(newVal.value.currentMediaItem)

            if (newVal.value.currentMediaItem) {
              sendHeartbeat(newVal.value.currentMediaItem, lastKnownPositionSeconds)
            }
          }

          if (newVal.value.playerState === "idle") {
            audioPlayerRef.current.src = ""
          }
        }
      }),
    )
  }, [audioPlayerRef, videoPlayerRef])

  videoAutoplay = hasInteracted.current && mediaPlayerState.playerState === "playing"

  return (
    <div
      style={playerStyle}
      className="bg-gray-300 text-sm lg:px-4 py-2 rounded-sm text-gray-700 grid lg:grid-cols-3 grid-cols-1 gap-12"
    >
      <div
        className={`overflow-hidden flex lg:block ${
          mobileView === "details" ? "" : "hidden"
        }`}
      >
        <div className={`${isMobile ? "grid grid-cols-12" : ""}`}>
          {isMobile && <div className="col-span-1"></div>}

          <div className={`${isMobile ? "col-span-10 flex" : "flex"}`}>
            {mediaPlayerState.currentMediaItem &&
            mediaPlayerState.currentMediaItem?.type === "audio" ? (
              <>
                <img
                  src={mediaPlayerState.currentMediaItem?.coverImageSmall}
                  style={{ maxHeight: "85px" }}
                />
                <div className="ml-2" style={metaDataStyle}>
                  <button
                    type="button"
                    className="font-bold flex items-center gap-1 text-left focus:outline-none"
                    onClick={openSongActionsForCurrent}
                    onMouseEnter={() => setTitleHovered(true)}
                    onMouseLeave={() => setTitleHovered(false)}
                  >
                    <span
                      dangerouslySetInnerHTML={{ __html: mediaPlayerState.currentMediaItem?.title }}
                    />
                    {titleHovered && <PlusCircle size={14} className="flex-none" />}
                  </button>
                  <div>
                    by{" "}
                    <Link
                      to={mediaPlayerState.currentMediaItem?.localPath}
                      className="font-bold"
                    >
                      {mediaPlayerState.currentMediaItem?.bandName}
                    </Link>
                  </div>
                  <div>
                    from{" "}
                    <Link
                      className="font-bold"
                      to={mediaPlayerState.currentMediaItem?.albumPath}
                    >
                      {mediaPlayerState.currentMediaItem?.albumName}
                    </Link>
                  </div>
                  {mediaPlayerState.currentMediaItem?.playlistName && (
                    <div>
                      <Link
                        className="font-bold text-bn-neon-red"
                        to={mediaPlayerState.currentMediaItem?.playlistPath || "/"}
                      >
                        {mediaPlayerState.currentMediaItem?.playlistName}
                      </Link>
                    </div>
                  )}
                </div>
              </>
            ) : mediaPlayerState.currentMediaItem &&
              mediaPlayerState.currentMediaItem?.type === "video" ? (
              <>
                <ReactPlayer
                  url={mediaPlayerState.currentMediaItem?.streamingUrl}
                  playing={videoAutoplay}
                  muted={false}
                  width="50%"
                  height="75px"
                  controls={false}
                  onError={onVideoError}
                  onEnded={onSkipClick}
                  onPlay={onVideoPlaying}
                  onPause={onVideoPause}
                  ref={videoPlayerRef}
                />
                {screenfull.isEnabled && (
                  <button
                    onClick={handleClickFullscreen}
                    className="ml-1 self-start"
                  >
                    <ArrowsFullscreen />
                  </button>
                )}
              </>
            ) : (
              <>
                <div></div>
              </>
            )}
          </div>

          {isMobile && (
            <div className="col-span-1">
              <button
                className="text-gray-500"
                style={{ transform: "translate(-21px, 30px) rotate(-90deg)" }}
                onClick={(e) => setMobileView("controls")}
              >
                CONTROLS
              </button>{" "}
            </div>
          )}
        </div>
      </div>
      <div className={`${mobileView === "controls" ? "" : "hidden"}`}>
        <div className={`${isMobile ? "grid grid-cols-12" : ""}`}>
          {isMobile && (
            <div className="col-span-1">
              <button
                className="text-gray-500"
                style={{ transform: "translate(-17px, 30px) rotate(90deg)" }}
                onClick={(e) => setMobileView("details")}
              >
                DETAILS
              </button>
            </div>
          )}

          <div className={`${isMobile ? "col-span-10" : ""}`}>
            <audio ref={audioPlayerRef} />{" "}
            <div style={{ display: "flex" }}>
              {audioPlayerRef && audioPlayerRef.current
                ? timeToMinutesSeconds(currentTime)
                : "-:--"}
              {audioPlayerRef &&
              audioPlayerRef.current &&
              mediaPlayerState &&
              mediaPlayerState.currentMediaItem ? (
                <Scrubber
                  current={currentTime}
                  max={mediaPlayerState.currentMediaItem?.duration}
                  playerCallback={scrubberUpdate}
                />
              ) : (
                <div
                  className="scrubber"
                  style={{
                    background: `linear-gradient(90deg, #dbdbdb 0% 0, #dbdbdb 0 100%)`,
                  }}
                ></div>
              )}
              {mediaPlayerState.currentMediaItem
                ? timeToMinutesSeconds(
                    mediaPlayerState.currentMediaItem?.duration,
                  )
                : "-:-"}
            </div>
            <div>
              <button
                onClick={onShuffleClick}
                className="mr-4 focus:outline-none"
                style={{ verticalAlign: "super" }}
                title="Shuffle"
              >
                <Shuffle
                  size={size * 0.7}
                  style={{ color: mediaPlayerState.shuffle ? "#e53e3e" : "inherit" }}
                />
              </button>
              <button
                onClick={(e) => onPreviousClick()}
                className="mr-2 focus:outline-none"
              >
                <SkipBackwardFill size={size} />
              </button>
              <button
                onClick={(e) => onPlayOrPauseClick()}
                className="focus:outline-none"
              >
                {mediaPlayerState.playerState === "playing" ? (
                  <PauseFill size={size} />
                ) : (
                  <PlayFill size={size} />
                )}
              </button>
              <button
                onClick={(e) => onSkipClick()}
                className="ml-2 focus:outline-none"
              >
                <SkipForwardFill size={size} />
              </button>
              <button
                onClick={onLoopClick}
                className="ml-4 focus:outline-none"
                style={{ verticalAlign: "super" }}
                title={loopTitle()}
              >
                {mediaPlayerState.loopMode === "song" ? (
                  <Repeat1 size={size * 0.7} style={{ color: "#e53e3e" }} />
                ) : (
                  <Repeat
                    size={size * 0.7}
                    style={{ color: mediaPlayerState.loopMode === "context" ? "#e53e3e" : "inherit" }}
                  />
                )}
              </button>
              <div
                className="font-bold leading-none"
                dangerouslySetInnerHTML={{
                  __html: mediaPlayerState?.currentMediaItem?.title,
                }}
              ></div>
            </div>
          </div>

          {isMobile && (
            <div className="col-span-1">
              <button
                className="text-gray-500"
                style={{ transform: "translate(-2px, 30px) rotate(-90deg)" }}
                onClick={(e) => setMobileView("queue")}
              >
                QUEUE
              </button>
            </div>
          )}
        </div>
      </div>
      <div
        className={`h-full text-left lg:block ${
          mobileView === "queue" ? "" : "hidden"
        }`}
      >
        <div className={`${isMobile ? "grid grid-cols-12" : ""}`}>
          {isMobile && (
            <div className="col-span-1">
              <button
                className="text-gray-500"
                style={{ transform: "translate(-28px, 33px) rotate(90deg)" }}
                onClick={(e) => setMobileView("controls")}
              >
                CONTROLS
              </button>
            </div>
          )}

          <div
            className={`${isMobile ? "col-span-10" : ""}`}
            style={{ marginTop: "-5px" }}
          >
            <div className="flex justify-between">
              <div>
                {mediaPlayerState.queue.length > 0 ? (
                  <div className="underline">
                    Queue
                    <button
                      className="pl-2 text-xs"
                      onClick={onClearQueueClick}
                    >
                      clear &times;
                    </button>
                  </div>
                ) : (
                  <span>Queue is empty</span>
                )}
              </div>
              <div>
                <button
                  className={clsx(styles.closeBtn, "focus:outline-none")}
                  onClick={onClearAndCloseClick}
                >
                  &times;
                </button>
              </div>
            </div>

            <ul
              className="text-sm overflow-y-scroll"
              style={{ height: "70px" }}
            >
              {mediaPlayerState.queue.map((content, index) => (
                <li key={`queue-${content.title}`} style={{ display: "flex" }}>
                  <span
                    className="text-xs"
                    style={{
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <XCircle
                      className="mr-1 cursor-pointer"
                      style={{ display: "inline-block" }}
                      onClick={() => onRemoveFromQueue(index)}
                    />
                    {timeToMinutesSeconds(content.duration)}{" "}
                    {content.type === "audio" ? (
                      <>
                        <span
                          className="font-bold"
                          dangerouslySetInnerHTML={{ __html: content.title }}
                        />{" "}
                        by {content.bandName}
                      </>
                    ) : (
                      <span
                        className="font-bold"
                        dangerouslySetInnerHTML={{ __html: content.title }}
                      />
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {isMobile && <div className="col-span-1"></div>}
        </div>
      </div>
    </div>
  )
}

export default UniversalPlayer
